export interface SanitizeUrlOptions {
	collapsePathPrefixes?: readonly (string | null)[];
}

function trimTrailingSlashes(value: string): string {
	return value.replace(
		/\/+$/,
		'',
	);
}

function normalizePathPrefix(
	prefix: string,
	requestUrl: string,
): string {
	try {
		return trimTrailingSlashes(sanitizeUrl(
			new URL(
				prefix,
				requestUrl,
			).toString(),
		));
	} catch {
		/*
		 * Browser SDKs normally supply absolute request URLs. Preserve the
		 * already-sanitized prefix when either boundary sends an invalid URL.
		 */
		return trimTrailingSlashes(sanitizeUrl(prefix));
	}
}

/**
 * Reduce a URL to a safe descriptor for error reporting: keep the origin and
 * path (which identify the resource) but drop anything that can carry secrets —
 * userinfo credentials and the query/hash, where signed-URL signatures, tokens
 * and cache-busting params live. `data:` URLs are collapsed to `data:[inline]`
 * so an inline payload is never echoed back. Relative or otherwise unparseable
 * inputs fall back to a best-effort strip of any authority userinfo, query and
 * hash.
 *
 * @param value A URL or URL-like string.
 * @param options Optional path prefixes whose dynamic descendants are reduced
 *   to the stable prefix for privacy and low-cardinality grouping.
 * @returns A safe descriptor: `data:[inline]`, or the origin+path with
 *   credentials, query and hash removed.
 */
export default function sanitizeUrl(
	value: string,
	options: SanitizeUrlOptions = {},
): string {
	/*
	 * Browser URL parsing trims surrounding whitespace before parsing, so match
	 * that here; otherwise a `   //user:pass@host` input would slip past both
	 * the data: check and the fallback authority regex.
	 */
	const trimmed = value.trim();

	/*
	 * A data: URL parses successfully but keeps its whole (potentially
	 * multi-megabyte or sensitive) payload in the pathname, so clearing
	 * search/hash would still echo it back. Collapse it instead. The scheme is
	 * case-insensitive. This lives here, not only in describeImageSource, so
	 * direct callers such as fontLoadError are covered too.
	 */
	if (/^data:/i.test(trimmed)) {
		return 'data:[inline]';
	}

	let sanitized: string;

	try {
		const url = new URL(trimmed);

		url.username = '';
		url.password = '';
		url.search = '';
		url.hash = '';

		sanitized = url.toString();
	} catch {
		/*
		 * Relative / protocol-relative / unparseable input: no base to resolve
		 * against, so best-effort strip any authority userinfo (credentials,
		 * e.g. `//user:pass@host`) and the query/hash.
		 */
		sanitized = trimmed
			.replace(
				/^((?:[a-z][a-z0-9+.-]*:)?\/\/)[^/?#]*@/i,
				'$1',
			)
			.split(/[?#]/)[0];
	}

	for (const prefix of options.collapsePathPrefixes ?? []) {
		if (!prefix) {
			continue;
		}

		const normalizedPrefix = normalizePathPrefix(
			prefix,
			sanitized,
		);

		if (
			sanitized === normalizedPrefix
			|| sanitized.startsWith(`${normalizedPrefix}/`)
		) {
			return normalizedPrefix;
		}
	}

	return sanitized;
}
